"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Toggle as TogglePrimitive } from "radix-ui";

import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP, type NxSize } from "../lib/onyx-material";

const toggleVariants = cva(
  "relative z-[1] inline-flex items-center justify-center gap-2 text-sm font-medium whitespace-nowrap transition-transform duration-150 ease-[cubic-bezier(0.34,1.45,0.6,1)] active:translate-y-px outline-none hover:bg-muted hover:text-muted-foreground focus-visible:[filter:drop-shadow(0_0_1px_hsl(var(--ring)))_drop-shadow(0_0_3px_hsl(var(--ring)/0.55))] disabled:pointer-events-none disabled:opacity-50 disabled:active:translate-y-0 aria-invalid:ring-destructive/20 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline:
          "bg-transparent hover:bg-accent hover:text-accent-foreground",
      },
      size: {
        default: "h-9 min-w-9 px-2",
        sm: "h-8 min-w-8 px-1.5",
        lg: "h-10 min-w-10 px-2.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

/**
 * Pressable button with an on/off state — in the Onyx notch language.
 *
 * Same `default`/`outline` variants + 3 sizes as upstream, but the box is the
 * hex-notch silhouette (`nx-b` `clip-path`, not `rounded-md`) with a continuous
 * `nx-rim` keyline ring (a real border is sliced at the notch, #158) and the
 * shared springy press — a 1px press-DOWN on `:active` with the overshoot
 * bounce every control uses. A toggle is a quiet, flat face (transparent at
 * rest, `--accent` fill when on), so it stays light: notch + keyline + press,
 * NO travel block. The pressed/on state reads via the `--accent` fill.
 *
 * Use for inline toolbar affordances (bold, italic, mute). For a
 * radio-style group, wrap multiple Toggles in `<ToggleGroup>`.
 */
function Toggle({
  className,
  variant,
  size,
  notch = "b",
  asChild = false,
  children,
  ...props
}: React.ComponentProps<typeof TogglePrimitive.Root> &
  VariantProps<typeof toggleVariants> & {
    /** Notch chamfer size. Default `b`. */
    notch?: NxSize;
  }) {
  return (
    <TogglePrimitive.Root
      data-slot="toggle"
      asChild={asChild}
      className={cn(nx[notch], toggleVariants({ variant, size, className }))}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {/* Notched keyline ring — traces the notch where a real border is
              sliced (#158). Keyed to --border so it stays neutral on the quiet
              face. Skipped when `asChild` (Slot needs a single child). */}
          <span
            aria-hidden
            className="nx-rim pointer-events-none absolute inset-0"
            style={{ "--nx-rim": "hsl(var(--border))", clipPath: NX_RING_CLIP[notch] } as React.CSSProperties}
          />
          {children}
        </>
      )}
    </TogglePrimitive.Root>
  );
}

export { Toggle, toggleVariants };
